import { useState, useCallback, useEffect, useRef } from 'react';
import { generatePresignedUrls } from '../actions/files/generatePresignedUrls';
import { getCurrentUserId } from '../utils/getCurrentUserId';
import { checkPdfPassword, validatePdfPassword } from '../utils/pdfPasswordDetector';
import { verifyMultiplePdfs, validatePdfPassword as validatePdfPasswordBackend } from '../actions/files/verifyPdf';
import { getDocumentChatStatus } from '../actions/document-chat';
import { useAuth } from '../contexts/AuthContext';

interface UseUploadFormOptions {
  minFiles?: number;
  maxFiles?: number;
  customErrorMessage?: string;
  maxTotalSizeMB?: number;
  maxSingleFileSizeMB?: number;
}

// Default file size limits
const DEFAULT_MAX_TOTAL_SIZE_MB = 60;
const DEFAULT_MAX_SINGLE_FILE_SIZE_MB = 30;
const BYTES_PER_MB = 1024 * 1024;

export const useUploadForm = (options: UseUploadFormOptions = {}) => {
  const {
    minFiles = 1,
    maxFiles = 50,
    customErrorMessage,
    maxTotalSizeMB = DEFAULT_MAX_TOTAL_SIZE_MB,
    maxSingleFileSizeMB = DEFAULT_MAX_SINGLE_FILE_SIZE_MB,
  } = options;

  const { setGateModalOpen } = useAuth();

  // Document chat access is now determined by backend (documentChatStatus?.isPremiumUser)

  // File upload state
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [useThinking, setUseThinking] = useState<boolean>(false);
  const [enableDocumentChat, setEnableDocumentChat] = useState<boolean>(false);
  
  // Job tracking state
  const [jobId, setJobId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  // Guard to prevent re-running API create call after client (4xx) errors
  const [clientErrorLock, setClientErrorLock] = useState<boolean>(false);
  
  // Document chat state (premium feature)
  const [documentChatStatus, setDocumentChatStatus] = useState<{
    chatEnabled: boolean;
    isPremiumUser: boolean;
  } | null>(null);
  const [documentChatOpen, setDocumentChatOpen] = useState(false);

  // PDF password protection state
  const [filePasswords, setFilePasswords] = useState<Record<string, string>>({});
  const [currentFileNeedingPassword, setCurrentFileNeedingPassword] = useState<File | null>(null);
  const [pendingPasswordFiles, setPendingPasswordFiles] = useState<File[]>([]);
  const [isCheckingPasswords, setIsCheckingPasswords] = useState<boolean>(false);
  
  // Ref to store filePasswords at submission time
  const filePasswordsRef = useRef<Record<string, string>>({});

  // Backend password request mechanism
  // When backend detects encrypted PDFs that client-side missed, we pause
  // the upload flow and show the password modal. These refs manage that flow.
  const backendPasswordResolveRef = useRef<((password: string) => void) | null>(null);
  const backendPasswordRejectRef = useRef<((error: Error) => void) | null>(null);
  const isBackendPasswordRequestRef = useRef<boolean>(false);
  const backendPasswordS3KeyRef = useRef<string>('');

  // File size utility functions
  const calculateTotalSize = useCallback((fileList: File[]): number => {
    return fileList.reduce((total, file) => total + file.size, 0);
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 MB';
    const mb = bytes / BYTES_PER_MB;
    return `${mb.toFixed(1)} MB`;
  }, []);

  const getCurrentTotalSize = useCallback((): number => {
    return calculateTotalSize(files);
  }, [files, calculateTotalSize]);

  const isOverSizeLimit = useCallback((): boolean => {
    return getCurrentTotalSize() > (maxTotalSizeMB * BYTES_PER_MB);
  }, [getCurrentTotalSize, maxTotalSizeMB]);

  const maxTotalSizeBytes = maxTotalSizeMB * BYTES_PER_MB;
  const maxSingleFileSizeBytes = maxSingleFileSizeMB * BYTES_PER_MB;

  // Get current user ID when hook mounts
  useEffect(() => {
    const getUserId = async () => {
      const currentUserId = await getCurrentUserId();
      setUserId(currentUserId || '');
    };
    getUserId();
  }, []);

  // Check document chat status for a job
  const checkDocumentChatStatus = useCallback(async (jobIdToCheck: string) => {
    if (!jobIdToCheck) return;
    try {
      const status = await getDocumentChatStatus(jobIdToCheck);
      setDocumentChatStatus({
        chatEnabled: status.chatEnabled,
        isPremiumUser: status.isPremiumUser
      });
    } catch (error) {
      console.warn('Failed to get document chat status:', error);
      setDocumentChatStatus(null);
    }
  }, []);

  // Handle opening chat
  const handleOpenDocumentChat = useCallback(() => {
    if (jobId) {
      setDocumentChatOpen(true);
    }
  }, [jobId]);

  // Handle closing chat
  const handleCloseDocumentChat = useCallback(() => {
    setDocumentChatOpen(false);
  }, []);

  // Enhanced drop handler with file size validation and password detection
  const createDropHandler = useCallback((isApproved: boolean) => {
    return async (acceptedFiles: File[]) => {
      if (!isApproved) {
        setGateModalOpen(true);
        return;
      }

      // Step 1: Check individual file size limits (most specific error first)
      const oversizedFiles = acceptedFiles.filter(f => f.size > maxSingleFileSizeBytes);
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles
          .map(f => `${f.name} (${(f.size / BYTES_PER_MB).toFixed(1)} MB)`)
          .join(', ');
        setFileError(
          `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${fileList}. ` +
          `Maximum allowed per file is ${maxSingleFileSizeMB} MB.`
        );
        return;
      }

      // Step 2: Check if adding these files would exceed the total batch size limit
      const currentSize = getCurrentTotalSize();
      const newFilesSize = calculateTotalSize(acceptedFiles);
      const projectedTotalSize = currentSize + newFilesSize;

      if (projectedTotalSize > maxTotalSizeBytes) {
        const currentSizeMB = formatFileSize(currentSize);
        const newFilesSizeMB = formatFileSize(newFilesSize);
        const projectedSizeMB = formatFileSize(projectedTotalSize);
        
        setFileError(
          `Cannot add files: Total size would be ${projectedSizeMB} (current: ${currentSizeMB} + new: ${newFilesSizeMB}). ` +
          `Maximum allowed is ${maxTotalSizeMB} MB. Please remove some files or choose smaller documents.`
        );
        return;
      }

      // Step 3: Check file count limits
      const projectedFileCount = files.length + acceptedFiles.length;
      if (projectedFileCount > maxFiles) {
        setFileError(`Cannot add ${acceptedFiles.length} files. Maximum allowed is ${maxFiles} files total.`);
        return;
      }

      // Step 4: Filter out duplicates
      const existingNames = new Set(files.map(f => f.name));
      const uniqueNewFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
      
      if (uniqueNewFiles.length === 0) {
        return; // All files are duplicates
      }

      // Step 5: Separate PDFs and non-PDFs
      const pdfFiles = uniqueNewFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
      const nonPdfFiles = uniqueNewFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));

      // Add non-PDF files immediately
      if (nonPdfFiles.length > 0) {
        setFiles(prev => [...prev, ...nonPdfFiles]);
      }

      // Step 6: Check PDFs for password protection
      if (pdfFiles.length > 0) {
        setIsCheckingPasswords(true);
        setFileError("");
        
        console.log(`[useUploadForm] Checking ${pdfFiles.length} PDF(s) for password protection...`);

        const passwordProtectedFiles: File[] = [];
        const unprotectedFiles: File[] = [];

        for (const file of pdfFiles) {
          try {
            console.log(`[useUploadForm] Checking: ${file.name}`);
            const result = await checkPdfPassword(file);
            console.log(`[useUploadForm] Result for ${file.name}:`, result);
            
            if (result.needsPassword) {
              console.log(`[useUploadForm] ${file.name} IS password-protected - will prompt for password`);
              passwordProtectedFiles.push(file);
            } else {
              console.log(`[useUploadForm] ${file.name} is NOT password-protected`);
              unprotectedFiles.push(file);
            }
          } catch (error) {
            console.error(`[useUploadForm] Error checking PDF password for ${file.name}:`, error);
            // On error, assume unprotected and let backend handle it
            // This is a fallback - ideally the checkPdfPassword function handles all errors
            unprotectedFiles.push(file);
          }
        }
        
        console.log(`[useUploadForm] Summary: ${passwordProtectedFiles.length} password-protected, ${unprotectedFiles.length} unprotected`);

        // Add unprotected PDFs immediately
        if (unprotectedFiles.length > 0) {
          setFiles(prev => [...prev, ...unprotectedFiles]);
        }

        // Queue password-protected files for password prompt
        if (passwordProtectedFiles.length > 0) {
          console.log(`[useUploadForm] Queuing ${passwordProtectedFiles.length} file(s) for password prompt`);
          const [firstFile, ...restFiles] = passwordProtectedFiles;
          setCurrentFileNeedingPassword(firstFile);
          setPendingPasswordFiles(restFiles);
        }

        setIsCheckingPasswords(false);
      }

      setFileError("");
    };
  }, [files, getCurrentTotalSize, calculateTotalSize, formatFileSize, maxSingleFileSizeBytes, maxSingleFileSizeMB, maxTotalSizeBytes, maxTotalSizeMB, maxFiles, setGateModalOpen]);

  // Password submission handler - supports both client-side and backend-detected encryption
  const handlePasswordSubmit = useCallback(async (password: string) => {
    if (!currentFileNeedingPassword) return;

    // Check if this is a backend password request (upload-time detection)
    if (isBackendPasswordRequestRef.current && backendPasswordS3KeyRef.current) {
      // Validate with backend API since client-side pdf.js couldn't detect the encryption
      const validation = await validatePdfPasswordBackend(backendPasswordS3KeyRef.current, password);
      
      if (validation.valid) {
        // Resolve the promise to unblock the upload handler
        if (backendPasswordResolveRef.current) {
          backendPasswordResolveRef.current(password);
        }
        // Reset backend password state
        isBackendPasswordRequestRef.current = false;
        backendPasswordS3KeyRef.current = '';
        backendPasswordResolveRef.current = null;
        backendPasswordRejectRef.current = null;
        setCurrentFileNeedingPassword(null);
      } else {
        throw new Error(validation.error || 'Incorrect password');
      }
      return;
    }

    // Client-side detected encryption - validate with pdf.js
    const result = await validatePdfPassword(currentFileNeedingPassword, password);
    
    if (result.valid) {
      // Add file to list and store password
      setFiles(prev => [...prev, currentFileNeedingPassword]);
      setFilePasswords(prev => ({
        ...prev,
        [currentFileNeedingPassword.name]: password
      }));

      // Move to next file needing password
      if (pendingPasswordFiles.length > 0) {
        const [nextFile, ...restFiles] = pendingPasswordFiles;
        setCurrentFileNeedingPassword(nextFile);
        setPendingPasswordFiles(restFiles);
      } else {
        setCurrentFileNeedingPassword(null);
      }
    } else {
      // Password invalid - the modal will handle showing the error
      throw new Error(result.error || 'Incorrect password');
    }
  }, [currentFileNeedingPassword, pendingPasswordFiles]);

  // Skip current password-protected file
  const handlePasswordSkip = useCallback(() => {
    // If this is a backend password request, reject the promise
    if (isBackendPasswordRequestRef.current && backendPasswordRejectRef.current) {
      backendPasswordRejectRef.current(new Error('User skipped password entry'));
      isBackendPasswordRequestRef.current = false;
      backendPasswordS3KeyRef.current = '';
      backendPasswordResolveRef.current = null;
      backendPasswordRejectRef.current = null;
      setCurrentFileNeedingPassword(null);
      return;
    }

    // Move to next file without adding current one
    if (pendingPasswordFiles.length > 0) {
      const [nextFile, ...restFiles] = pendingPasswordFiles;
      setCurrentFileNeedingPassword(nextFile);
      setPendingPasswordFiles(restFiles);
    } else {
      setCurrentFileNeedingPassword(null);
    }
  }, [pendingPasswordFiles]);

  // Cancel all pending password prompts
  const handlePasswordCancel = useCallback(() => {
    // If this is a backend password request, reject the promise
    if (isBackendPasswordRequestRef.current && backendPasswordRejectRef.current) {
      backendPasswordRejectRef.current(new Error('User cancelled password entry'));
      isBackendPasswordRequestRef.current = false;
      backendPasswordS3KeyRef.current = '';
      backendPasswordResolveRef.current = null;
      backendPasswordRejectRef.current = null;
    }
    
    setCurrentFileNeedingPassword(null);
    setPendingPasswordFiles([]);
  }, []);

  // Enhanced file removal with size limit clearing and password cleanup
  const removeFile = useCallback((fileToRemove: File) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.filter((file) => file.name !== fileToRemove.name);
      
      // Clear error if removing files brings us under the limit
      const newTotalSize = calculateTotalSize(newFiles);
      if (newTotalSize <= maxTotalSizeBytes && fileError.includes('Total size')) {
        setFileError("");
      }
      
      return newFiles;
    });
    
    // Also remove password if stored
    setFilePasswords(prev => {
      const newPasswords = { ...prev };
      delete newPasswords[fileToRemove.name];
      return newPasswords;
    });
  }, [calculateTotalSize, maxTotalSizeBytes, fileError]);

  // Enhanced form validation
  const validateForm = useCallback((): boolean => {
    if (files.length < minFiles) {
      setFileError(customErrorMessage || `Please upload at least ${minFiles} file${minFiles > 1 ? 's' : ''}.`);
      return false;
    }

    if (isOverSizeLimit()) {
      setFileError(`Total file size (${formatFileSize(getCurrentTotalSize())}) exceeds the ${maxTotalSizeMB} MB limit. Please remove some files.`);
      return false;
    }

    setFileError("");
    return true;
  }, [files.length, minFiles, customErrorMessage, isOverSizeLimit, formatFileSize, getCurrentTotalSize, maxTotalSizeMB]);

  // Enhanced form submission handler - stores passwords at submission time
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (clientErrorLock) {
      alert('Cannot submit: previous request failed due to a client error. Please refresh the page or adjust your inputs and try again.');
      return;
    }
    if (validateForm()) {
      // Store current passwords for use in upload handler
      filePasswordsRef.current = { ...filePasswords };
      setProcessing(true);
      setShowLoader(true);
    }
  }, [validateForm, clientErrorLock, filePasswords]);

  // Generic upload handler creator - now includes password support
  const createApiUploadHandler = useCallback((
    apiCall: (fileKeys: string[], filePasswords?: Record<string, string>) => Promise<any>,
    onSuccessCallback?: () => void
  ) => {
    return async (
      filesToUpload: File[],
      setProgressCallback: (progress: number) => void
    ): Promise<string> => {
      try {
        if (clientErrorLock) {
          throw new Error('Previous request failed due to a client error. Please adjust inputs or try again later.');
        }

        if (filesToUpload.length === 0) {
          const response = await apiCall([], undefined);
          const extractedJobId = response.data?.jobId || response.jobId;

          if (!extractedJobId || extractedJobId.trim() === "") {
            throw new Error("Invalid server response: Missing job identifier.");
          }

          setJobId(extractedJobId);
          onSuccessCallback?.();
          return response.data?.message || response.message || "Process started successfully.";
        }

        // Step 1: Get presigned URLs
        const presignedUrls = await generatePresignedUrls(
          filesToUpload.map((file) => ({
            filename: file.name,
            contentType: file.type,
            size: file.size,
          }))
        );

        let currentProgress = 0;
        const progressIncrement = 50 / filesToUpload.length;

        // Step 2: Upload files
        await Promise.all(
          presignedUrls.map(async (urlObj) => {
            const file = filesToUpload.find(f => f.name === urlObj.filename);
            if (!file) throw new Error(`File not found: ${urlObj.filename}`);

            const response = await fetch(urlObj.url, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file,
            });

            if (!response.ok) {
              throw new Error(`Upload failed for file: ${file.name}`);
            }

            currentProgress += progressIncrement;
            setProgressCallback(currentProgress);
          })
        );

        // Step 3: Build file passwords map keyed by S3 key
        const s3KeyPasswords: Record<string, string> = {};
        presignedUrls.forEach((urlObj) => {
          const password = filePasswordsRef.current[urlObj.filename];
          if (password) {
            s3KeyPasswords[urlObj.key] = password;
          }
        });

        // Step 3.5: Backend PDF verification (catches encryption frontend might miss)
        // If backend detects encryption, pause and show password modal to user
        const pdfUrls = presignedUrls.filter(
          urlObj => urlObj.filename.toLowerCase().endsWith('.pdf')
        );
        
        if (pdfUrls.length > 0) {
          console.log(`[useUploadForm] Backend verification for ${pdfUrls.length} PDF(s)...`);
          
          // Collect info about encrypted PDFs that need passwords
          const encryptedFiles: Array<{ urlObj: typeof pdfUrls[0]; file: File }> = [];

          // Split into files that already have passwords vs. those that need to be checked
          const urlsWithPassword = pdfUrls.filter(urlObj => !!s3KeyPasswords[urlObj.key]);
          const urlsWithoutPassword = pdfUrls.filter(urlObj => !s3KeyPasswords[urlObj.key]);

          // Validate pre-supplied passwords individually (each needs a unique password param)
          await Promise.all(
            urlsWithPassword.map(async (urlObj) => {
              const existingPassword = s3KeyPasswords[urlObj.key];
              const validation = await validatePdfPasswordBackend(urlObj.key, existingPassword);
              if (!validation.valid) {
                console.warn(`[useUploadForm] Backend rejected password for ${urlObj.filename}`);
                const matchedFile = filesToUpload.find(f => f.name === urlObj.filename);
                if (matchedFile) {
                  encryptedFiles.push({ urlObj, file: matchedFile });
                }
              }
            })
          );

          // Verify all password-free PDFs in a single batch request
          if (urlsWithoutPassword.length > 0) {
            const batchResults = await verifyMultiplePdfs(
              urlsWithoutPassword.map(u => u.key)
            );
            for (const urlObj of urlsWithoutPassword) {
              const result = batchResults.get(urlObj.key);
              if (result?.needsPassword) {
                console.log(`[useUploadForm] Backend detected encryption for ${urlObj.filename}`);
                const matchedFile = filesToUpload.find(f => f.name === urlObj.filename);
                if (matchedFile) {
                  encryptedFiles.push({ urlObj, file: matchedFile });
                }
              }
            }
          }
          
          // If backend found encrypted PDFs, prompt user for passwords one by one
          if (encryptedFiles.length > 0) {
            console.log(`[useUploadForm] Prompting user for ${encryptedFiles.length} password(s) via modal...`);
            
            for (const { urlObj, file } of encryptedFiles) {
              try {
                // Create a promise that will be resolved when user enters password in the modal
                const password = await new Promise<string>((resolve, reject) => {
                  backendPasswordResolveRef.current = resolve;
                  backendPasswordRejectRef.current = reject;
                  isBackendPasswordRequestRef.current = true;
                  backendPasswordS3KeyRef.current = urlObj.key;
                  // This triggers the PdfPasswordModal to open
                  setCurrentFileNeedingPassword(file);
                });
                
                // Password was validated and accepted - store it
                s3KeyPasswords[urlObj.key] = password;
                console.log(`[useUploadForm] Password accepted for ${urlObj.filename}`);
              } catch (err: any) {
                // User skipped or cancelled - throw to abort upload
                console.warn(`[useUploadForm] Password not provided for ${urlObj.filename}:`, err.message);
                throw new Error(
                  `🔒 Password required for: ${urlObj.filename}\n\n` +
                  `This file is encrypted and requires a password to process. ` +
                  `Please close this dialog and try again, providing the correct password when prompted.`
                );
              }
            }
          }
        }

        // Step 4: Call API with file keys and passwords
        setProgressCallback(60);
        
        const passwordsToSend = Object.keys(s3KeyPasswords).length > 0 ? s3KeyPasswords : undefined;
        const response = await apiCall(presignedUrls.map(obj => obj.key), passwordsToSend);
        
        // Step 5: Validate response and extract jobId
        const extractedJobId = response.data?.jobId || response.jobId;
        
        if (!extractedJobId || extractedJobId.trim() === "") {
          console.error("❌ API Response validation failed - missing jobId:", response);
          throw new Error("Invalid server response: Missing job identifier. Please close this dialog and resubmit your request, or contact support if the issue persists.");
        }

        setJobId(extractedJobId);
        
        setProgressCallback(100);

        // Clear form and call success callback
        // NOTE: We don't clear form here anymore, we clear it in handleModalClose
        // clearFiles();
        onSuccessCallback?.();

        return response.data?.message || response.message || "Processing started successfully.";
      } catch (error: any) {
        const status: number | undefined = error?.response?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          setClientErrorLock(true);
          setProcessing(false);
          setShowLoader(false);
          const message = error?.response?.data?.message || 'Request failed. Please check your inputs and try again.';
          alert(message);
        }
        throw error;
      }
    };
  }, [clientErrorLock]);

  // Clear files utility - also clears passwords and backend password state
  const clearFiles = useCallback(() => {
    setFiles([]);
    setFileError("");
    setFilePasswords({});
    setEnableDocumentChat(true); // Reset to default (true)
    setCurrentFileNeedingPassword(null);
    setPendingPasswordFiles([]);
    filePasswordsRef.current = {};
    // Reset backend password request state
    isBackendPasswordRequestRef.current = false;
    backendPasswordS3KeyRef.current = '';
    backendPasswordResolveRef.current = null;
    backendPasswordRejectRef.current = null;
  }, []);

  // Modal close handler
  const handleModalClose = useCallback(() => {
    setShowLoader(false);
    setProcessing(false);
    clearFiles();
    setJobId('');
    setDocumentChatStatus(null);
    setRefresh(true);
  }, [clearFiles]);

  // Refresh completion handler
  const handleRefreshComplete = useCallback(() => {
    setRefresh(false);
  }, []);

  // File size info for UI display
  const fileSizeInfo = {
    currentSize: getCurrentTotalSize(),
    currentSizeMB: formatFileSize(getCurrentTotalSize()),
    maxSizeMB: maxTotalSizeMB,
    maxSizeBytes: maxTotalSizeBytes,
    isOverLimit: isOverSizeLimit(),
    progressPercentage: Math.min((getCurrentTotalSize() / maxTotalSizeBytes) * 100, 100)
  };

  // Whether password modal should be shown
  const showPasswordModal = currentFileNeedingPassword !== null;

  // Computed property for document chat access
  const hasDocumentChatAccess = documentChatStatus?.isPremiumUser || false;

  return {
    // File state
    files,
    fileError,
    processing,
    showLoader,
    refresh,
    useThinking,
    enableDocumentChat,
    jobId,
    userId,

    // Document chat state
    documentChatOpen,
    documentChatStatus,
    hasDocumentChatAccess, // Computed: isPremiumUser from backend
    handleOpenDocumentChat,
    handleCloseDocumentChat,
    checkDocumentChatStatus,

    // File size info
    fileSizeInfo,

    // Password state
    filePasswords,
    currentFileNeedingPassword,
    showPasswordModal,
    isCheckingPasswords,

    // Setters
    setFiles,
    setFileError,
    setProcessing,
    setShowLoader,
    setRefresh,
    setUseThinking,
    setEnableDocumentChat,
    setJobId,
    setUserId,
    setDocumentChatOpen,

    // Handlers
    createDropHandler,
    removeFile,
    handleSubmit,
    createApiUploadHandler,
    clearFiles,
    handleModalClose,
    handleRefreshComplete,

    // Password handlers
    handlePasswordSubmit,
    handlePasswordSkip,
    handlePasswordCancel,

    // Validation
    validateForm,
    isOverSizeLimit,

    // Utilities
    formatFileSize,
    getCurrentTotalSize
  };
};
